<template>
    <div class="sequenced-note-player">
        <div class="controls-section" v-if="!hideControls">
            <v-btn
                v-if="!isListening"
                color="primary"
                @click="startListening"
                :disabled="isComplete"
            >
                Start Playing
            </v-btn>
            <v-btn v-else color="error" @click="stopListening"> Stop </v-btn>
            <v-btn
                v-if="isComplete"
                color="success"
                @click="reset"
                class="ml-2"
            >
                Play Again
            </v-btn>
        </div>

        <div
            class="pitch-visualizer-section"
            v-if="showPitchVisualizer && isListening"
        >
            <PitchOffsetVisualizer
                :cents-off="currentCentsOff"
                :max-display-cents="toleranceCents"
                :variant="pitchVisualizerVariant"
                :show-detected="isPitchDetected"
                :show-cents="false"
                orientation="horizontal"
            />
        </div>

        <div class="sequence-display">
            <MusicPhrase
                :notes="noteStates"
                :clef="clef"
                :width="width"
                :height="height"
                :show-fingerings="showFingerings"
                :fingering-width="fingeringWidth"
                :fingering-height="fingeringHeight"
                :time-signature="timeSignature"
                :key-signature="keySignature"
                :enforce-time-signature="enforceTimeSignature"
                :overlay="noteOverlay"
            />
        </div>

        <!-- Adaptive fingering display (only when showFingerings=false) -->
        <transition name="fade-slide">
            <v-card
                v-show="
                    enableAdaptiveFingering &&
                    !showFingerings &&
                    showAdaptiveFingering !== null
                "
                class="adaptive-fingering-container"
            >
                <div class="help-label">Try:</div>
                <FluteFingering
                    :note="adaptiveFingeringNote ?? undefined"
                    :fingering="adaptiveFingering"
                    :height="adaptiveFingeringHeight"
                    orientation="horizontal"
                />
            </v-card>
        </transition>

        <div class="progress-section" v-if="!hideProgress">
            <v-progress-linear
                :model-value="progress * 100"
                color="primary"
                height="8"
                rounded
            />
            <div class="progress-text">
                {{ currentIndex }} / {{ sequence.length }} notes
                <span v-if="isComplete" class="text-success ml-2"
                    >✓ Complete!</span
                >
            </div>
        </div>

        <div
            class="attempts-display"
            v-if="showAttemptsPips && maxWrongAttempts > 0"
        >
            <div
                v-for="pip in maxWrongAttempts"
                :key="pip"
                class="attempt-pip"
                :class="{ used: wrongAttempts >= pip }"
            ></div>
        </div>

        <div class="feedback-section" v-if="currentFeedback && !hideStatus">
            <v-alert
                :type="currentFeedback.type"
                :text="currentFeedback.message"
                density="compact"
                closable
                @click:close="currentFeedback = null"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import MusicPhrase, {
    type NoteData,
    type NoteOverlay,
} from "./MusicPhrase.vue";
import PitchOffsetVisualizer from "./PitchOffsetVisualizer.vue";
import FluteFingering from "./FluteFingering.vue";
import {
    SequencePitchMatcher,
    type SequenceMatchEvent,
    type PitchDetectionResult,
} from "../pitch";
import {
    createPitchDetector,
    DetectorType,
    NoteName,
    pitchResultToMidi,
} from "../pitch";
import {
    noteToMidi,
    getStandardFingering,
    type FingeringState,
} from "../data/fluteFingeringChart";
import { accidental } from "../pitch/noteUtils";
import { logOptimistic } from "../utils/debugLog";

interface Props {
    sequence: NoteName[];
    toleranceCents?: number;
    strictCents?: boolean;
    allowOctaveErrors?: boolean;
    dropoutBufferFrames?: number;
    showFingerings?: boolean;
    clef?: string;
    duration?: string;
    autoStart?: boolean;
    hideControls?: boolean;
    hideProgress?: boolean;
    hideStatus?: boolean;
    width?: number;
    height?: number;
    fingeringWidth?: number;
    fingeringHeight?: number;
    enforceTimeSignature?: boolean; // Use time signature validation (default: false for free mode)
    timeSignature?: string; // e.g., "4/4", "3/4"
    keySignature?: string; // e.g., "C", "G", "D", "F", "Bb"
    showPitchVisualizer?: boolean; // Show pitch offset visualizer
    pitchVisualizerVariant?: "default" | "minimal"; // Visualizer style
    maxWrongAttempts?: number; // Maximum wrong attempts before failing (default: 5, 0 = unlimited)
    showAttemptsPips?: boolean; // Show attempt pips display (default: true)
    enableAdaptiveFingering?: boolean; // Toggle adaptive fingering feature on/off (default: true)
    adaptiveFingeringThreshold?: number; // Wrong attempts before showing fingering help (default: 2)
    adaptiveFingeringWidth?: number; // Width of horizontal adaptive fingering chart (default: 200)
    adaptiveFingeringHeight?: number; // Height of horizontal adaptive fingering chart (default: 80)
}

const props = withDefaults(defineProps<Props>(), {
    toleranceCents: 40,
    strictCents: true,
    allowOctaveErrors: false,
    dropoutBufferFrames: 5,
    showFingerings: true,
    clef: "treble",
    duration: "quarter",
    autoStart: false,
    hideControls: false,
    hideProgress: false,
    hideStatus: false,
    width: 800,
    height: 275,
    fingeringWidth: 110,
    fingeringHeight: 120,
    enforceTimeSignature: false, // Default to free mode (no time signature validation)
    timeSignature: "4/4",
    keySignature: undefined,
    showPitchVisualizer: false, // Default off
    pitchVisualizerVariant: "minimal", // Default to minimal style
    maxWrongAttempts: 5, // Fail after 5 wrong attempts
    showAttemptsPips: true, // Show pips by default
    enableAdaptiveFingering: true, // Feature enabled by default
    adaptiveFingeringThreshold: 2, // Show after 2 wrong attempts
    adaptiveFingeringWidth: 200, // Larger horizontal display
    adaptiveFingeringHeight: 80,
});

const emit = defineEmits<{
    "status-change": [status: string, event: SequenceMatchEvent | null];
    "note-matched": [note: string, index: number];
    "wrong-note": [detected: string, expected: string];
    "note-ended": [note: string];
    "sequence-complete": [wrongAttempts: number];
    "sequence-failed": [wrongAttempts: number];
    "pitch-detected": [result: PitchDetectionResult];
    "fingering-help-triggered": [
        data: {
            noteIndex: number;
            expectedNote: string;
            detectedNote: string;
            totalAttempts: number;
        },
    ];
}>();

// Audio state
const audioContext = ref<AudioContext | null>(null);
const analyser = ref<AnalyserNode | null>(null);
const mediaStream = ref<MediaStream | null>(null);
const detector = createPitchDetector(DetectorType.BLUESHARP, "flute");
const isListening = ref(false);
const animationFrameId = ref<number | null>(null);

// Matcher state
const matcher = ref<SequencePitchMatcher | null>(null);
const currentIndex = ref(0);
const isComplete = ref(false);
const progress = ref(0);
const wrongAttempts = ref(0);

// Visual feedback state
const currentFeedback = ref<{
    type: "error" | "success" | "warning";
    message: string;
} | null>(null);
const incorrectNoteIndex = ref<number | null>(null);
const flashTimeoutId = ref<number | null>(null);

// Current pitch detection state (for overlay)
const currentDetectedMidi = ref<number | null>(null);
const showWrongNoteOverlay = ref(false);

// Sustaining note tracking (for shimmer effect)
const sustainingNoteIndex = ref<number | null>(null);

// Pitch offset for visualizer
const currentCentsOff = ref<number>(0);
const isPitchDetected = ref<boolean>(false);

// Track if waiting for user to release final note before completing
const waitingForRelease = ref(false);
const pendingCompletionAttempts = ref<number>(0);

// Track if waiting for silence after failure before dismissing
const waitingForSilence = ref(false);
const silenceFrameCount = ref<number>(0);
const SILENCE_FRAMES_REQUIRED = 10; // ~10 frames of silence (~166ms at 60fps)

// Adaptive fingering help state
const noteWrongAttempts = ref<Map<number, number>>(new Map());
const showAdaptiveFingering = ref<number | null>(null); // null or note index

// Optimistic UI state - show correct pitch immediately, confirm after buffer
const optimisticCorrectPitch = ref<boolean>(false);

// Computed overlay for showing wrong note feedback
const noteOverlay = computed<NoteOverlay | undefined>(() => {
    if (
        !showWrongNoteOverlay.value ||
        currentDetectedMidi.value === null ||
        isComplete.value ||
        optimisticCorrectPitch.value // Hide overlay immediately when correct pitch detected
    ) {
        return undefined;
    }

    return {
        noteIndex: currentIndex.value,
        playedNote: { midi: currentDetectedMidi.value },
        color: "#F44336", // Red for incorrect
    };
});

// Computed note states for visual feedback
const noteStates = computed<NoteData[]>(() => {
    return props.sequence.map((noteStr, index) => {
        const isCompleted = index < currentIndex.value;
        const isCurrent = index === currentIndex.value && !isComplete.value;
        const isIncorrect = index === incorrectNoteIndex.value;
        const isSustaining =
            index === sustainingNoteIndex.value ||
            (index === currentIndex.value &&
                optimisticCorrectPitch.value &&
                isListening.value);

        // console.log(`[SNP] noteStr: ${noteStr}`);

        // Convert note string to MIDI
        const midi = noteToMidi(noteStr);
        if (midi === undefined) {
            console.warn(`Could not convert note "${noteStr}" to MIDI`);
            return {
                midi: 60, // Fallback to C4
                duration: props.duration,
                opacity: 1.0,
            };
        }

        return {
            midi,
            duration: props.duration,
            // Sustaining takes priority over completed styling
            color: isSustaining
                ? undefined
                : isCompleted
                  ? "gray"
                  : isCurrent
                    ? undefined
                    : "black",
            opacity: isSustaining ? 1.0 : isCompleted ? 0.3 : 1.0,
            highlight: isCurrent,
            incorrect: isIncorrect,
            sustaining: isSustaining,
            spelling: accidental(noteStr),
        };
    });
});

// Computed properties for adaptive fingering
const adaptiveFingeringNote = computed<string | null>(() => {
    if (showAdaptiveFingering.value === null) return null;
    const noteIndex = showAdaptiveFingering.value;
    if (noteIndex < 0 || noteIndex >= props.sequence.length) return null;
    return props.sequence[noteIndex];
});

const adaptiveFingering = computed<FingeringState>(() => {
    if (!adaptiveFingeringNote.value) return {};
    return getStandardFingering(adaptiveFingeringNote.value) || {};
});

// Initialize matcher
function initMatcher() {
    matcher.value = new SequencePitchMatcher({
        sequence: props.sequence,
        toleranceCents: props.toleranceCents,
        strictCents: props.strictCents,
        allowOctaveErrors: props.allowOctaveErrors,
        dropoutBufferFrames: props.dropoutBufferFrames,
    });
    updateState();
}

// Start listening
async function startListening() {
    try {
        // Request microphone access
        mediaStream.value = await navigator.mediaDevices.getUserMedia({
            audio: {
                echoCancellation: false,
                noiseSuppression: false,
                autoGainControl: false,
            },
        });

        // Create audio context
        audioContext.value = new AudioContext();
        const source = audioContext.value.createMediaStreamSource(
            mediaStream.value,
        );

        // Create analyzer
        analyser.value = audioContext.value.createAnalyser();
        analyser.value.fftSize = 2048;
        analyser.value.smoothingTimeConstant = 0.8;
        source.connect(analyser.value);

        isListening.value = true;
        emit("status-change", "listening", null);

        // Start analysis loop
        analyzePitch();
    } catch (error) {
        console.error("Failed to access microphone:", error);
        currentFeedback.value = {
            type: "error",
            message: "Could not access microphone. Please check permissions.",
        };
    }
}

// Stop listening
function stopListening() {
    isListening.value = false;

    if (animationFrameId.value !== null) {
        cancelAnimationFrame(animationFrameId.value);
        animationFrameId.value = null;
    }

    if (mediaStream.value) {
        mediaStream.value.getTracks().forEach((track) => track.stop());
        mediaStream.value = null;
    }

    if (audioContext.value) {
        audioContext.value.close();
        audioContext.value = null;
    }

    analyser.value = null;
    emit("status-change", "idle", null);
}

// Pitch analysis loop
function analyzePitch() {
    if (
        !isListening.value ||
        !analyser.value ||
        !audioContext.value ||
        !matcher.value
    ) {
        return;
    }

    // Get audio data
    const buffer = new Float32Array(analyser.value.frequencyBinCount);
    analyser.value.getFloatTimeDomainData(buffer);

    // Detect pitch
    const result: PitchDetectionResult = detector.detectPitch(
        buffer,
        audioContext.value.sampleRate,
    );

    // Update current detected MIDI for overlay
    currentDetectedMidi.value = pitchResultToMidi(result);

    // Update pitch offset for visualizer
    if (result.frequency && result.centsOff !== undefined) {
        currentCentsOff.value = result.centsOff;
        isPitchDetected.value = true;
    } else {
        isPitchDetected.value = false;
    }

    // Check for silence when waiting after failure
    if (waitingForSilence.value) {
        if (!result.frequency) {
            // No pitch detected - increment silence counter
            silenceFrameCount.value++;

            if (silenceFrameCount.value >= SILENCE_FRAMES_REQUIRED) {
                // Sufficient silence detected - complete the failure
                waitingForSilence.value = false;
                isComplete.value = true;

                currentFeedback.value = {
                    type: "error",
                    message: `Too many wrong attempts! ${wrongAttempts.value} wrong attempts`,
                };
                emit("sequence-failed", wrongAttempts.value);
                stopListening();
                return; // Exit early - no need to continue analysis
            }
        } else {
            // Pitch detected - reset silence counter
            silenceFrameCount.value = 0;
        }
    }

    // Optimistic check: does detected pitch match expected note?
    // This enables instant visual feedback while buffer confirms
    if (!isComplete.value && !waitingForRelease.value && !waitingForSilence.value) {
        const expectedNote = props.sequence[currentIndex.value];
        const expectedMidi = noteToMidi(expectedNote);
        const detectedMidi = currentDetectedMidi.value;

        if (
            expectedMidi !== undefined &&
            detectedMidi !== null &&
            result.frequency
        ) {
            // Check if notes match (considering octave errors if allowed)
            let matches = detectedMidi === expectedMidi;
            if (props.allowOctaveErrors && !matches) {
                matches = detectedMidi % 12 === expectedMidi % 12;
            }

            // Check cents tolerance if strict cents is enabled
            if (matches && props.strictCents && result.centsOff !== undefined) {
                matches = Math.abs(result.centsOff) <= props.toleranceCents;
            }

            // Log when optimistic state changes to correct
            // if (matches && !optimisticCorrectPitch.value) {
                // logOptimistic("b. First correct pitch detected (optimistic)");
            // }

            optimisticCorrectPitch.value = matches;
        } else {
            optimisticCorrectPitch.value = false;
        }
    } else {
        optimisticCorrectPitch.value = false;
    }

    // Emit for recording
    emit("pitch-detected", result);

    // Process with matcher
    const event = matcher.value.processPitch(result);

    if (event) {
        handleMatcherEvent(event);
        emit("status-change", event.type, event);
    }

    // Continue loop
    animationFrameId.value = requestAnimationFrame(analyzePitch);
}

// Handle matcher events
function handleMatcherEvent(event: SequenceMatchEvent) {
    switch (event.type) {
        case "note-matched":
            // logOptimistic("c. Note ratified as correct by matcher");

            // Hide overlay on correct note
            showWrongNoteOverlay.value = false;
            // Note: Don't clear sustainingNoteIndex here - let note-sustained handle it
            // This prevents the shimmer from disappearing during quick note transitions

            currentFeedback.value = {
                type: "success",
                message: `✓ ${event.note}`,
            };

            // NEW: Hide adaptive fingering when note is correct
            if (showAdaptiveFingering.value === event.index) {
                showAdaptiveFingering.value = null;
            }

            emit("note-matched", event.note, event.index);
            updateState();
            break;

        case "wrong-note":
            // Show overlay with wrong note
            showWrongNoteOverlay.value = true;

            // Flash the current note red briefly (keep existing behavior)
            incorrectNoteIndex.value = event.index;
            if (flashTimeoutId.value !== null) {
                clearTimeout(flashTimeoutId.value);
            }
            flashTimeoutId.value = window.setTimeout(() => {
                incorrectNoteIndex.value = null;
            }, 300);

            // Distinguish between wrong note vs out of tune
            let message: string;
            let feedbackType: "error" | "warning" = "error";
            if (event.detected === event.expected) {
                // Same note name - it's a tuning issue
                const direction =
                    event.centsOff && event.centsOff > 0
                        ? "sharp (too high)"
                        : "flat (too low)";
                message = `Out of tune - ${direction}`;
                feedbackType = "warning";
            } else {
                // Different note names - actually wrong note
                message = `Wrong note: played ${event.detected}, expected ${event.expected}`;
            }

            currentFeedback.value = {
                type: feedbackType,
                message,
            };
            emit("wrong-note", event.detected, event.expected);
            updateState();

            // NEW: Track attempts for adaptive fingering
            if (props.enableAdaptiveFingering && !props.showFingerings) {
                const currentIndex = event.index;
                const attempts = noteWrongAttempts.value.get(currentIndex) || 0;
                noteWrongAttempts.value.set(currentIndex, attempts + 1);

                if (attempts + 1 >= props.adaptiveFingeringThreshold) {
                    showAdaptiveFingering.value = currentIndex;

                    emit("fingering-help-triggered", {
                        noteIndex: currentIndex,
                        expectedNote: event.expected,
                        detectedNote: event.detected,
                        totalAttempts: attempts + 1,
                    });
                }
            }

            // Check if exceeded max wrong attempts
            if (
                props.maxWrongAttempts > 0 &&
                wrongAttempts.value > props.maxWrongAttempts
            ) {
                handleSequenceFailed();
            }
            break;

        case "sequence-complete":
            // Hide overlay on completion
            showWrongNoteOverlay.value = false;

            // Don't complete yet - wait for user to release the final note
            waitingForRelease.value = true;
            pendingCompletionAttempts.value = event.totalAttempts || 0;

            currentFeedback.value = {
                type: "success",
                message: `Sequence complete! Release the note...`,
            };
            updateState();
            // Don't set isComplete or stopListening yet - wait for note-ended
            break;

        case "note-sustained":
            // Hide overlay when sustaining correct note
            showWrongNoteOverlay.value = false;
            // Show sustaining shimmer on the previous note (the one being held)
            sustainingNoteIndex.value = event.index;
            break;

        case "note-ended":
            // Clear sustaining shimmer when note is released
            sustainingNoteIndex.value = null;

            // Emit note-ended event for tracking
            emit("note-ended", event.note);

            // If waiting for release after sequence completion, finalize now
            if (waitingForRelease.value) {
                waitingForRelease.value = false;
                isComplete.value = true;

                currentFeedback.value = {
                    type: "success",
                    message: `Sequence complete! ${pendingCompletionAttempts.value} wrong attempts`,
                };
                emit("sequence-complete", pendingCompletionAttempts.value);
                updateState();
                stopListening();
            }
            break;
    }
}

// Update state from matcher
function updateState() {
    if (matcher.value) {
        const state = matcher.value.getState();
        currentIndex.value = state.currentIndex;
        isComplete.value = state.isComplete;
        progress.value = state.progress;
        wrongAttempts.value = state.wrongAttempts || 0;
    }
}

// Handle sequence failure (too many wrong attempts)
function handleSequenceFailed() {
    // Don't complete yet - wait for silence first
    waitingForSilence.value = true;
    showWrongNoteOverlay.value = false;
    sustainingNoteIndex.value = null;

    currentFeedback.value = {
        type: "error",
        message: `Too many wrong attempts! Release the note...`,
    };
    // Don't emit sequence-failed or stopListening yet - wait for silence
}

// Reset to beginning
function reset() {
    stopListening();
    matcher.value?.reset();
    updateState();
    currentFeedback.value = null;
    incorrectNoteIndex.value = null;
    currentDetectedMidi.value = null;
    showWrongNoteOverlay.value = false;
    sustainingNoteIndex.value = null;
    waitingForRelease.value = false;
    pendingCompletionAttempts.value = 0;
    waitingForSilence.value = false;
    silenceFrameCount.value = 0;
    optimisticCorrectPitch.value = false;

    // Reset adaptive fingering state
    noteWrongAttempts.value.clear();
    showAdaptiveFingering.value = null;

    if (flashTimeoutId.value !== null) {
        clearTimeout(flashTimeoutId.value);
        flashTimeoutId.value = null;
    }
}

// Watch for sequence changes
watch(
    () => props.sequence,
    () => {
        reset();
        initMatcher();
    },
    { deep: true },
);

// Lifecycle
onMounted(() => {
    initMatcher();
    if (props.autoStart) {
        startListening();
    }
});

onUnmounted(() => {
    stopListening();
    if (flashTimeoutId.value !== null) {
        clearTimeout(flashTimeoutId.value);
    }
});
</script>

<style scoped>
.sequenced-note-player {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.controls-section {
    display: flex;
    gap: 8px;
}

.sequence-display {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
}

.progress-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.progress-text {
    text-align: center;
    font-size: 0.9rem;
    color: rgba(0, 0, 0, 0.6);
}

.feedback-section {
    min-height: 48px;
}

.pitch-visualizer-section {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px 0;
}

.attempts-display {
    display: flex;
    justify-content: center;
    gap: 8px;
    padding: 10px 0;
}

.attempt-pip {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: rgb(var(--v-theme-accent));
    transition: all 0.3s ease;
}

.attempt-pip.used {
    background: #e9ecef;
    transform: scale(0.7);
    opacity: 0.5;
}

.adaptive-fingering-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 12px 0;
    /* Pull closer to staff - eliminates gap from vertical fingering space */
    margin-top: -7em;
}

.help-label {
    font-size: 14px;
    font-weight: 600;
    color: rgb(var(--v-theme-primary));
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* Fade-slide transition - now works reliably with v-show */
.fade-slide-enter-active {
    transition:
        opacity 0.3s ease-out,
        transform 0.3s ease-out;
}

.fade-slide-leave-active {
    transition:
        opacity 0.2s ease-in,
        transform 0.2s ease-in;
}

.fade-slide-enter-from {
    opacity: 0;
    transform: translateY(-8px);
}

.fade-slide-leave-to {
    opacity: 0;
    transform: translateY(8px);
}
</style>
